import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

const readChoice = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

const saveChoice = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch (e) {
    // Storage can be unavailable (private mode); the choice just isn't remembered
  }
};

/**
 * Button that runs the last-used option, with a chevron that opens the full list.
 * The chosen option is remembered per browser under `storageKey`.
 */
// doneId / doneLabel: briefly confirm the action that just ran on the button itself
// disabled: nothing to act on yet; both halves are off and the menu stays shut
export const SplitButton = ({ options, storageKey, onSelect, label, doneId, doneLabel, disabled }) => {
  const [open, setOpen] = useState(false);
  const [currentId, setCurrentId] = useState(options[0].id);
  const ref = useRef(null);

  useEffect(() => {
    const saved = readChoice(storageKey);
    if (options.some((option) => option.id === saved)) setCurrentId(saved);
  }, [storageKey]);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (event.type === "keydown" && event.key !== "Escape") return;
      if (event.type === "mousedown" && ref.current.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
    };
  }, [open]);

  const current =
    options.find((option) => option.id === currentId) || options[0];

  const run = (option) => {
    setOpen(false);
    setCurrentId(option.id);
    saveChoice(storageKey, option.id);
    onSelect(option.id);
  };

  return (
    <div className={`split-btn${open && !disabled ? " open" : ""}${disabled ? " is-disabled" : ""}`} ref={ref}>
      <button
        type="button"
        className={`split-btn__main${doneId === current.id ? " is-done" : ""}`}
        onClick={() => run(current)}
        disabled={disabled}
      >
        {doneId === current.id ? (
          <React.Fragment>
            <CheckMark />
            {doneLabel}
          </React.Fragment>
        ) : (
          current.label
        )}
      </button>
      <button
        type="button"
        className="split-btn__toggle"
        aria-haspopup="menu"
        aria-expanded={open && !disabled}
        aria-label={label}
        onClick={() => setOpen(!open)}
        disabled={disabled}
      >
        <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
          <path
            d="M2.5 7.5 6 4l3.5 3.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && !disabled ? (
        <ul className="split-btn__menu" role="menu">
          {options.map((option) => (
            <li key={option.id} role="none">
              <button
                type="button"
                role="menuitem"
                className={option.id === current.id ? "active" : ""}
                onClick={() => run(option)}
              >
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

export const CheckMark = () => (
  <svg className="check-mark" width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
    <path
      d="M3 8.5 6.5 12 13 4.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

SplitButton.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string, label: PropTypes.string })
  ).isRequired,
  storageKey: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
  label: PropTypes.string,
  doneId: PropTypes.string,
  doneLabel: PropTypes.string,
  disabled: PropTypes.bool,
};

SplitButton.defaultProps = {
  label: "More options",
  doneId: null,
  doneLabel: "Done",
};

export default SplitButton;
